import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { X } from 'lucide-react';

const TrafficSourceFilter = ({ onCampaignsChange }) => {
  const { projectId, trafficSource, updateTrafficSource } = useDashboard();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState(() => {
    // Загружаем сохраненные выбранные кампании из localStorage
    const saved = localStorage.getItem('selectedCampaigns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved campaigns:', e);
      }
    }
    return trafficSource.campaigns || [];
  });
  const [searchText, setSearchText] = useState(() => {
    // Загружаем сохраненный фильтр из localStorage
    return localStorage.getItem('campaignsSearchText') || '';
  });

  // Загрузка списка кампаний (перезагружаем при смене проекта)
  useEffect(() => {
    const loadCampaigns = async () => {
      setLoading(true);
      try {
        // Передаем projectId в запрос
        const url = projectId 
          ? `/api/campaigns?projectId=${projectId}`
          : '/api/campaigns';
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setCampaigns(data);
          
          // При смене проекта сбрасываем выбранные кампании
          // так как кампании разные для разных проектов
          console.log(`📋 Loaded ${data.length} campaigns for project: ${projectId || 'shortly'}`);
        }
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, [projectId]); // Перезагружаем при смене проекта
  
  // Сбрасываем выбранные кампании при СМЕНЕ проекта. На первом маунте НЕ
  // сбрасываем — иначе затрётся trafficSource.campaigns, который DashboardContext
  // только что восстановил из localStorage.
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current === projectId) return;
    prevProjectIdRef.current = projectId;
    setSelectedCampaigns([]);
    updateTrafficSource({
      campaign: 'all',
      campaigns: [],
      mediaSource: 'all'
    });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Источник правды — DashboardContext.trafficSource.campaigns. Если он меняется
  // снаружи (например, из виджета сравнения когорт), локальное состояние сайдбара
  // должно подхватить новое значение.
  useEffect(() => {
    const applied = trafficSource.campaigns || [];
    setSelectedCampaigns(prev => {
      if (prev.length === applied.length && prev.every((c, i) => c === applied[i])) {
        return prev;
      }
      return applied;
    });
  }, [trafficSource.campaigns]);

  // Фильтрация кампаний по тексту
  const filteredCampaigns = campaigns.filter(camp => 
    camp.campaign.toLowerCase().includes(searchText.toLowerCase())
  );

  // Снимаем галочки с кампаний, которые скрылись при фильтрации
  // Но только при активном изменении фильтра, не при загрузке
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    // Помечаем, что начальная загрузка завершена после загрузки кампаний
    if (campaigns.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [campaigns, isInitialLoad]);

  useEffect(() => {
    // Снимаем галочки только если это не начальная загрузка
    if (searchText && !isInitialLoad) {
      const visibleCampaignNames = filteredCampaigns.map(c => c.campaign);
      const newSelected = selectedCampaigns.filter(campaignName => 
        visibleCampaignNames.includes(campaignName)
      );
      
      if (newSelected.length !== selectedCampaigns.length) {
        setSelectedCampaigns(newSelected);
      }
    }
  }, [searchText, filteredCampaigns, selectedCampaigns, isInitialLoad]);

  // Сохраняем фильтр в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('campaignsSearchText', searchText);
  }, [searchText]);

  // Сохраняем выбранные кампании в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('selectedCampaigns', JSON.stringify(selectedCampaigns));
  }, [selectedCampaigns]);

  // Уведомляем родительский компонент об изменениях
  useEffect(() => {
    if (onCampaignsChange) {
      onCampaignsChange(selectedCampaigns);
    }
  }, [selectedCampaigns, onCampaignsChange]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleCampaignToggle = (campaignName) => {
    const newSelected = selectedCampaigns.includes(campaignName)
      ? selectedCampaigns.filter(c => c !== campaignName)
      : [...selectedCampaigns, campaignName];
    
    setSelectedCampaigns(newSelected);
  };

  const handleSelectAll = () => {
    const allCampaigns = filteredCampaigns.map(c => c.campaign);
    setSelectedCampaigns([...new Set([...selectedCampaigns, ...allCampaigns])]);
  };

  const handleClearAll = () => {
    setSelectedCampaigns([]);
  };

  const isFiltered = selectedCampaigns.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Заголовок - фиксированный */}
      <div className="flex-shrink-0 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Кампании</h3>
          </div>
          {isFiltered && (
            <Badge variant="secondary" className="text-xs">
              {selectedCampaigns.length}
            </Badge>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Загрузка...</div>
      ) : (
        <>
          {/* Поиск и кнопки - фиксированные */}
          <div className="flex-shrink-0 space-y-3 mb-3">
            {/* Поле поиска */}
            <div className="space-y-2">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Поиск кампаний..."
                  value={searchText}
                  onChange={handleSearchChange}
                  className="h-8 text-xs pr-8"
                />
                {searchText && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={handleClearSearch}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {searchText && (
                <div className="text-xs text-muted-foreground">
                  Найдено: {filteredCampaigns.length}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSelectAll}
                className="flex-1 h-8 text-xs"
              >
                Выбрать все
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleClearAll}
                className="flex-1 h-8 text-xs"
              >
                Снять все
              </Button>
            </div>
          </div>

          {/* Список кампаний - растягивается */}
          <div className="flex-1 min-h-0 border rounded-md">
            <ScrollArea className="h-full">
              <div className="p-2 space-y-1">
                {filteredCampaigns.length === 0 && searchText ? (
                  <div className="text-xs text-muted-foreground py-2 text-center">
                    Ничего не найдено
                  </div>
                ) : (
                  filteredCampaigns.map(camp => {
                    const isRestricted = camp.campaign.toLowerCase().includes('restricted');
                    const isSelected = selectedCampaigns.includes(camp.campaign);
                    
                    return (
                      <div 
                        key={camp.campaign}
                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-accent cursor-pointer"
                        onClick={() => handleCampaignToggle(camp.campaign)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleCampaignToggle(camp.campaign)}
                          className="h-4 w-4"
                        />
                        <span className="text-xs flex-1 truncate">
                          {camp.campaign}
                        </span>
                        {isRestricted && (
                          <Badge variant="outline" className="text-xs px-1 py-0">
                            огр.
                          </Badge>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  );
};

export default TrafficSourceFilter;
